'use client';

import { CheckCircle2, Coffee, LogIn, LogOut, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';

import type {
	AttendanceBreakRecordRequest,
	AttendanceBreakResponse,
	AttendanceResponse,
	AttendanceWorkMode,
	UpdateTodayAttendanceRequest
} from '$lib/api/types';
import { getAttendanceActionState, type AttendanceActionKind } from '$lib/attendance/attendance-actions';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Label,
	Spinner
} from '$lib/ui';
import { cn } from '$lib/utils';

function timeLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const timePart = value.includes('T') ? value.split('T')[1] : value;
	return timePart?.slice(0, 5) || '-';
}

function minutesLabel(minutes: number | null | undefined): string {
	if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return '0분';
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	if (hours === 0) return `${rest}분`;
	if (rest === 0) return `${hours}시간`;
	return `${hours}시간 ${rest}분`;
}

function timeInputValue(value: string | null | undefined): string {
	if (!value) return '';
	const timePart = value.includes('T') ? value.split('T')[1] : value;
	return timePart?.slice(0, 5) || '';
}

function breakRangeLabel(breakItem: AttendanceBreakResponse): string {
	return `${timeLabel(breakItem.startedAt)} - ${breakItem.active ? '진행 중' : timeLabel(breakItem.endedAt)}`;
}

function breakRequestFromForm(startedAt: string, endedAt: string, note: string): AttendanceBreakRecordRequest {
	return {
		startedAt,
		endedAt,
		note: note.trim() || null
	};
}

function effectiveBreakMinutes(attendance: AttendanceResponse | null): number | null | undefined {
	const hasBreakRecords = (attendance?.breaks?.length ?? 0) > 0;
	if (hasBreakRecords || attendance?.onBreak) {
		return attendance?.recordedBreakMinutes ?? 0;
	}
	return attendance?.breakMinutes ?? attendance?.recordedBreakMinutes;
}

const WORK_MODE_OPTIONS: { value: AttendanceWorkMode; label: string }[] = [
	{ value: 'OFFICE', label: '사무실' },
	{ value: 'REMOTE', label: '재택' },
	{ value: 'FIELD', label: '현장' }
];

function normalizeWorkMode(value: AttendanceResponse['workMode'] | undefined): AttendanceWorkMode {
	if (value === 'REMOTE' || value === 'FIELD') return value;
	return 'OFFICE';
}

function workModeLabel(value: AttendanceWorkMode): string {
	return WORK_MODE_OPTIONS.find((option) => option.value === value)?.label ?? '사무실';
}

function actionIcon(kind: AttendanceActionKind) {
	if (kind === 'check-in') return <LogIn className="size-4" />;
	if (kind === 'check-out') return <LogOut className="size-4" />;
	if (kind === 'break-start' || kind === 'break-end') return <Coffee className="size-4" />;
	return <CheckCircle2 className="size-4" />;
}

export function AttendanceActionPanel({
	attendance,
	busy,
	loading = false,
	error,
	className,
	onAction,
	onUpdate,
	onCreateBreak,
	onUpdateBreak,
	onDeleteBreak
}: {
	attendance: AttendanceResponse | null;
	busy: boolean;
	loading?: boolean;
	error?: string | null;
	className?: string;
	onAction: (kind: AttendanceActionKind, options?: { workMode?: AttendanceWorkMode }) => void;
	onUpdate?: (
		request: UpdateTodayAttendanceRequest
	) => Promise<AttendanceResponse | null | void> | AttendanceResponse | null | void;
	onCreateBreak?: (
		request: AttendanceBreakRecordRequest
	) => Promise<AttendanceResponse | null | void> | AttendanceResponse | null | void;
	onUpdateBreak?: (
		breakId: number,
		request: AttendanceBreakRecordRequest
	) => Promise<AttendanceResponse | null | void> | AttendanceResponse | null | void;
	onDeleteBreak?: (breakId: number) => Promise<AttendanceResponse | null | void> | AttendanceResponse | null | void;
}) {
	const action = getAttendanceActionState(attendance);
	const workHours = attendance?.workHoursFormatted ?? minutesLabel(attendance?.workMinutes);
	const isWorking = Boolean(attendance?.checkInTime && !attendance?.checkOutTime);
	const canEdit = Boolean(isWorking && onUpdate);
	const canManageBreak = isWorking;
	const canManageBreakRecords = isWorking && Boolean(onCreateBreak || onUpdateBreak || onDeleteBreak);
	const displayedBreakMinutes = effectiveBreakMinutes(attendance);
	const breakHistory = attendance?.breaks ?? [];
	const currentWorkMode = normalizeWorkMode(attendance?.workMode);
	const breakActionKind: AttendanceActionKind = attendance?.onBreak ? 'break-end' : 'break-start';
	const breakActionLabel = attendance?.onBreak ? '휴게 종료' : '휴게 시작';
	const statusLabel = attendance?.onBreak
		? '휴게 중'
		: action.kind === 'check-in'
			? '출근 전'
			: action.kind === 'check-out'
				? '근무 중'
				: '완료';
	const statusVariant = attendance?.onBreak ? 'warning' : action.kind === 'complete' ? 'success' : 'outline';
	const [editing, setEditing] = useState(false);
	const [checkInTime, setCheckInTime] = useState(timeInputValue(attendance?.checkInTime));
	const [breakMinutes, setBreakMinutes] = useState(String(attendance?.breakMinutes ?? 0));
	const [note, setNote] = useState(attendance?.note ?? '');
	const [selectedWorkMode, setSelectedWorkMode] = useState<AttendanceWorkMode>(currentWorkMode);
	const [editWorkMode, setEditWorkMode] = useState<AttendanceWorkMode>(currentWorkMode);
	const [breakFormMode, setBreakFormMode] = useState<'create' | 'edit' | null>(null);
	const [editingBreakId, setEditingBreakId] = useState<number | null>(null);
	const [breakStartedAt, setBreakStartedAt] = useState('');
	const [breakEndedAt, setBreakEndedAt] = useState('');
	const [breakNote, setBreakNote] = useState('');

	function startEditing() {
		setCheckInTime(timeInputValue(attendance?.checkInTime));
		setBreakMinutes(String(displayedBreakMinutes ?? 0));
		setNote(attendance?.note ?? '');
		setEditWorkMode(currentWorkMode);
		setEditing(true);
	}

	function startCreateBreak() {
		setBreakFormMode('create');
		setEditingBreakId(null);
		setBreakStartedAt('');
		setBreakEndedAt('');
		setBreakNote('');
	}

	function startEditBreak(breakItem: AttendanceBreakResponse) {
		if (breakItem.id == null) return;
		setBreakFormMode('edit');
		setEditingBreakId(breakItem.id);
		setBreakStartedAt(timeInputValue(breakItem.startedAt));
		setBreakEndedAt(timeInputValue(breakItem.endedAt));
		setBreakNote(breakItem.note ?? '');
	}

	async function submitEdit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!onUpdate || !checkInTime) return;
		const result = await onUpdate({
			checkInTime,
			breakMinutes: Math.max(0, Number(breakMinutes) || 0),
			shiftId: attendance?.selectedShiftId ?? null,
			workMode: editWorkMode,
			note: note.trim() || null
		});
		if (result !== null) {
			setEditing(false);
		}
	}

	async function submitBreakRecord(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (breakFormMode === 'create' && onCreateBreak) {
			const result = await onCreateBreak(breakRequestFromForm(breakStartedAt, breakEndedAt, breakNote));
			if (result !== null) setBreakFormMode(null);
			return;
		}
		if (breakFormMode === 'edit' && editingBreakId != null && onUpdateBreak) {
			const result = await onUpdateBreak(
				editingBreakId,
				breakRequestFromForm(breakStartedAt, breakEndedAt, breakNote)
			);
			if (result !== null) {
				setBreakFormMode(null);
				setEditingBreakId(null);
			}
		}
	}

	return (
		<Card className={cn('min-h-full', className)}>
			<CardHeader className="pb-3">
				<div className="flex items-start justify-between gap-3">
					<div>
						<CardTitle>오늘 출퇴근</CardTitle>
						<CardDescription>{action.description}</CardDescription>
					</div>
					<div className="flex flex-wrap justify-end gap-2">
						{attendance?.checkInTime ? <Badge variant="secondary">{workModeLabel(currentWorkMode)}</Badge> : null}
						<Badge variant={statusVariant}>{statusLabel}</Badge>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
					<div className="rounded-lg bg-muted/30 p-3">
						<p className="text-xs text-muted-foreground">출근</p>
						<p className="mt-1 text-base font-semibold">{timeLabel(attendance?.checkInTime)}</p>
					</div>
					<div className="rounded-lg bg-muted/30 p-3">
						<p className="text-xs text-muted-foreground">퇴근</p>
						<p className="mt-1 text-base font-semibold">{timeLabel(attendance?.checkOutTime)}</p>
						{attendance?.scheduledCheckOutTime ? (
							<p className="mt-1 text-xs text-muted-foreground">예정 {timeLabel(attendance.scheduledCheckOutTime)}</p>
						) : null}
					</div>
					<div className="rounded-lg bg-muted/30 p-3">
						<p className="text-xs text-muted-foreground">근무</p>
						<p className="mt-1 text-base font-semibold">{workHours}</p>
					</div>
					<div
						className={cn(
							'rounded-lg p-3',
							attendance?.onBreak ? 'border border-warning/30 bg-warning/10' : 'bg-muted/30'
						)}
					>
						<p className="text-xs text-muted-foreground">휴게</p>
						<p className="mt-1 text-base font-semibold">{minutesLabel(displayedBreakMinutes)}</p>
						{attendance?.onBreak && attendance.activeBreakStartedAt ? (
							<p className="mt-1 text-xs text-warning-foreground">
								시작 {timeLabel(attendance.activeBreakStartedAt)}
							</p>
						) : null}
					</div>
				</div>
				{error ? (
					<div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
						{error}
					</div>
				) : null}
				{breakHistory.length > 0 || canManageBreakRecords ? (
					<div className="rounded-lg border border-border bg-muted/15 p-3">
						<div className="flex items-center justify-between gap-3">
							<p className="text-xs font-semibold text-muted-foreground">휴게 이력</p>
							<div className="flex items-center gap-2">
								<Badge variant="outline">{breakHistory.length}건</Badge>
								{isWorking && onCreateBreak ? (
									<Button
										type="button"
										variant="outline"
										size="sm"
										disabled={loading || busy}
										onClick={startCreateBreak}
									>
										<Plus className="size-3.5" />
										휴게 추가
									</Button>
								) : null}
							</div>
						</div>
						{breakFormMode ? (
							<form className="mt-3 rounded-lg border border-border bg-background p-3" onSubmit={submitBreakRecord}>
								<div className="grid gap-3 sm:grid-cols-3">
									<div className="space-y-1.5">
										<Label htmlFor="attendance-break-start">휴게 시작</Label>
										<Input
											id="attendance-break-start"
											type="time"
											value={breakStartedAt}
											onChange={(event) => setBreakStartedAt(event.target.value)}
											required
										/>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="attendance-break-end">휴게 종료</Label>
										<Input
											id="attendance-break-end"
											type="time"
											value={breakEndedAt}
											onChange={(event) => setBreakEndedAt(event.target.value)}
											required
										/>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="attendance-break-note">휴게 메모</Label>
										<Input
											id="attendance-break-note"
											value={breakNote}
											onChange={(event) => setBreakNote(event.target.value)}
											maxLength={500}
										/>
									</div>
								</div>
								<div className="mt-3 grid grid-cols-2 gap-2">
									<Button
										type="button"
										variant="outline"
										onClick={() => setBreakFormMode(null)}
										disabled={busy}
									>
										<X className="size-4" />
										취소
									</Button>
									<Button type="submit" disabled={busy || loading}>
										{busy ? <Spinner size="sm" /> : <Save className="size-4" />}
										{breakFormMode === 'create' ? '휴게 저장' : '휴게 수정 저장'}
									</Button>
								</div>
							</form>
						) : null}
						<div className="mt-2 divide-y divide-border">
							{breakHistory.map((breakItem, index) => (
								<div
									key={breakItem.id ?? `${breakItem.startedAt}-${index}`}
									className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
								>
									<div className="min-w-0">
										<p className="text-sm font-medium">{breakRangeLabel(breakItem)}</p>
										{breakItem.note ? (
											<p className="mt-0.5 truncate text-xs text-muted-foreground">{breakItem.note}</p>
										) : null}
									</div>
									<div className="flex shrink-0 items-center gap-2">
										{breakItem.active ? <Badge variant="warning">진행 중</Badge> : null}
										<span className="text-sm font-semibold">{minutesLabel(breakItem.durationMinutes)}</span>
										{isWorking && !breakItem.active && breakItem.id != null && onUpdateBreak ? (
											<Button
												type="button"
												variant="ghost"
												size="sm"
												disabled={loading || busy}
												onClick={() => startEditBreak(breakItem)}
											>
												<Pencil className="size-3.5" />
												휴게 수정
											</Button>
										) : null}
										{isWorking && breakItem.id != null && onDeleteBreak ? (
											<Button
												type="button"
												variant="ghost"
												size="sm"
												disabled={loading || busy}
												onClick={() => void onDeleteBreak(breakItem.id as number)}
											>
												<Trash2 className="size-3.5" />
												휴게 삭제
											</Button>
										) : null}
									</div>
								</div>
							))}
						</div>
					</div>
				) : null}
				{editing ? (
					<form className="rounded-lg border border-border bg-muted/20 p-3" onSubmit={submitEdit}>
						<div className="grid gap-3 sm:grid-cols-3">
							<div className="space-y-1.5">
								<Label htmlFor="attendance-edit-check-in">출근 시각</Label>
								<Input
									id="attendance-edit-check-in"
									type="time"
									value={checkInTime}
									onChange={(event) => setCheckInTime(event.target.value)}
									required
								/>
							</div>
							<div className="space-y-1.5">
								<Label htmlFor="attendance-edit-break">휴게 시간</Label>
								<Input
									id="attendance-edit-break"
									type="number"
									min={0}
									max={1440}
									value={breakMinutes}
									onChange={(event) => setBreakMinutes(event.target.value)}
								/>
							</div>
							<div className="space-y-1.5">
								<Label htmlFor="attendance-edit-note">메모</Label>
								<Input
									id="attendance-edit-note"
									value={note}
									onChange={(event) => setNote(event.target.value)}
									maxLength={500}
								/>
							</div>
							<div className="space-y-1.5 sm:col-span-3">
								<Label>근무 방식</Label>
								<div className="grid grid-cols-3 gap-2">
									{WORK_MODE_OPTIONS.map((option) => (
										<Button
											key={option.value}
											type="button"
											variant={editWorkMode === option.value ? 'default' : 'outline'}
											size="sm"
											aria-pressed={editWorkMode === option.value}
											disabled={busy || loading}
											onClick={() => setEditWorkMode(option.value)}
										>
											{option.label}
										</Button>
									))}
								</div>
							</div>
						</div>
						<div className="mt-3 grid grid-cols-2 gap-2">
							<Button type="button" variant="outline" onClick={() => setEditing(false)} disabled={busy}>
								<X className="size-4" />
								취소
							</Button>
							<Button type="submit" disabled={busy || loading}>
								{busy ? <Spinner size="sm" /> : <Save className="size-4" />}
								수정 저장
							</Button>
						</div>
					</form>
				) : (
					<div className="space-y-3">
						{action.kind === 'check-in' ? (
							<div className="grid grid-cols-3 gap-2">
								{WORK_MODE_OPTIONS.map((option) => (
									<Button
										key={option.value}
										type="button"
										variant={selectedWorkMode === option.value ? 'default' : 'outline'}
										size="sm"
										aria-pressed={selectedWorkMode === option.value}
										disabled={loading || busy}
										onClick={() => setSelectedWorkMode(option.value)}
									>
										{option.label}
									</Button>
								))}
							</div>
						) : null}
						<div
							className={cn(
								'grid gap-2',
								canEdit && canManageBreak
									? 'sm:grid-cols-[1fr_1fr_1.2fr]'
									: canEdit || canManageBreak
										? 'sm:grid-cols-[1fr_1.4fr]'
										: ''
							)}
						>
							{canEdit ? (
								<Button
									type="button"
									variant="outline"
									size="lg"
									disabled={loading || busy}
									onClick={startEditing}
								>
									<Pencil className="size-4" />
									기록 수정
								</Button>
							) : null}
							{canManageBreak ? (
								<Button
									type="button"
									variant={attendance?.onBreak ? 'default' : 'outline'}
									size="lg"
									disabled={loading || busy}
									onClick={() => onAction(breakActionKind)}
								>
									{busy ? <Spinner size="sm" /> : actionIcon(breakActionKind)}
									{breakActionLabel}
								</Button>
							) : null}
							<Button
								type="button"
								className="w-full"
								size="lg"
								disabled={loading || busy || action.disabled}
								onClick={() =>
									action.kind === 'check-in'
										? selectedWorkMode === 'OFFICE'
											? onAction(action.kind)
											: onAction(action.kind, { workMode: selectedWorkMode })
										: onAction(action.kind)
								}
							>
								{loading || busy ? <Spinner size="sm" /> : actionIcon(action.kind)}
								{loading ? '확인 중' : busy ? '처리 중' : action.label}
							</Button>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
